import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import { Alert } from '@material-ui/lab';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useAsync } from 'react-use';
import { Progress } from '@backstage/core-components';

import {
  evaluateEntity,
  evaluateEntityRuleCategories,
  NormalizedRule,
  RuleSeverity,
} from '../utils/maturityEvaluator';
import { rules } from '../rules/rules';
import { MaturityProgressBar } from './MaturityProgressBar';

type AggregatedGap = {
  rule: NormalizedRule;
  count: number;
};

const severityOrder: Record<RuleSeverity, number> = {
  critical: 0,
  major: 1,
  minor: 2,
};

export const SystemMaturitySummaryCard = () => {
  const { entity } = useEntity();
  const theme = useTheme();
  const catalogApi = useApi(catalogApiRef);

  if (!entity || entity.kind?.toLocaleLowerCase('en-US') !== 'system') {
    return null;
  }

  const systemName = entity.metadata.name;

  const { value, loading, error } = useAsync(async () => {
    if (!systemName) {
      return {
        componentCount: 0,
        passed: 0,
        total: 0,
        passedWeight: 0,
        totalWeight: 0,
        gaps: [] as AggregatedGap[],
      };
    }

    const { items } = await catalogApi.getEntities({
      filter: { kind: 'Component', 'spec.system': systemName },
    });

    let passedChecks = 0;
    let totalChecks = 0;
    let passedWeight = 0;
    let totalWeight = 0;
    const gapMap = new Map<string, AggregatedGap>();

    items.forEach(componentEntity => {
      const evaluation = evaluateEntity(componentEntity, rules);
      passedChecks += evaluation.passed;
      totalChecks += evaluation.total;
      passedWeight += evaluation.passedWeight;
      totalWeight += evaluation.totalWeight;

      const categories = evaluateEntityRuleCategories(componentEntity, rules);
      categories.forEach(category => {
        category.missingChecks.forEach(rule => {
          const key = rule.name;
          const current = gapMap.get(key);
          if (current) {
            current.count += 1;
          } else {
            gapMap.set(key, { rule, count: 1 });
          }
        });
      });
    });

    const gaps = Array.from(gapMap.values()).sort((a, b) => {
      const severityDiff = severityOrder[a.rule.severity] - severityOrder[b.rule.severity];
      if (severityDiff !== 0) return severityDiff;
      if (b.rule.weight !== a.rule.weight) return b.rule.weight - a.rule.weight;
      if (b.count !== a.count) return b.count - a.count;
      return a.rule.name.localeCompare(b.rule.name);
    });

    return {
      componentCount: items.length,
      passed: passedChecks,
      total: totalChecks,
      passedWeight,
      totalWeight,
      gaps,
    };
  }, [catalogApi, systemName]);

  const severityColors: Record<RuleSeverity, string> = {
    critical: theme.palette.error.main,
    major: theme.palette.warning.main,
    minor: theme.palette.text.secondary,
  };

  const severityLabels: Record<RuleSeverity, string> = {
    critical: 'Critical',
    major: 'Major',
    minor: 'Minor',
  };

  const kind = (entity.kind ?? 'System').toLocaleLowerCase('en-US');
  const ns = entity.metadata.namespace ?? 'default';
  const name = entity.metadata.name ?? '';
  const maturityPath = `/catalog/${ns}/${kind}/${name}/maturity`;

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Maturity Snapshot
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Aggregate maturity for components in this system and the most common gaps.
        </Typography>

        {loading ? (
          <Box mt={2}>
            <Progress />
          </Box>
        ) : error ? (
          <Box mt={2}>
            <Alert severity="error">Failed to load maturity data.</Alert>
          </Box>
        ) : value ? (
          <>
            <Box mt={2}>
              <Typography variant="caption" color="textSecondary">
                {value.componentCount} component{value.componentCount === 1 ? '' : 's'} tracked
              </Typography>
              <MaturityProgressBar
                passed={value.passed}
                total={value.total}
                passedWeight={value.passedWeight}
                totalWeight={value.totalWeight}
              />
            </Box>

            {value.gaps.length > 0 ? (
              <>
                <Typography variant="subtitle2" gutterBottom>
                  Most common gaps
                </Typography>
                <List dense>
                  {value.gaps.slice(0, 4).map(entry => (
                    <ListItem key={entry.rule.name} disableGutters>
                      <ListItemIcon style={{ minWidth: 32 }}>
                        <HighlightOffIcon
                          fontSize="small"
                          style={{ color: severityColors[entry.rule.severity] }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primaryTypographyProps={{ variant: 'body2' }}
                        primary={
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <span>{entry.rule.name}</span>
                            <Typography
                              variant="caption"
                              style={{ color: severityColors[entry.rule.severity] }}
                            >
                              {severityLabels[entry.rule.severity]}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <>
                            {entry.rule.guidance && (
                              <Typography variant="caption" color="textSecondary" display="block">
                                {entry.rule.guidance}
                              </Typography>
                            )}
                            <Typography variant="caption" color="textSecondary">
                              {entry.count} component{entry.count === 1 ? '' : 's'} missing
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            ) : (
              <Typography variant="body2" color="textSecondary">
                All tracked checks are satisfied across the system.
              </Typography>
            )}

            <Box mt={2} display="flex" flexWrap="wrap" justifyContent="flex-end">
              <Button
                component="a"
                href={maturityPath}
                size="small"
                color="primary"
                variant="contained"
                style={{ marginBottom: 8 }}
              >
                View maturity report
              </Button>
            </Box>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
};
